import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DragDropContext, Draggable, Droppable, DroppableStateSnapshot, DroppableProvided } from 'react-beautiful-dnd';
import produce from 'immer';

import { RootState } from '../../modules';
import { editContent } from '../../modules/editor';

import FieldSection from './FieldSection';
import Select from '../../components/Dropdown/Select';
import close from '../../assets/images/icon/close.png';

import { service } from '../../config';

import dragHandle from '../../assets/images/icon/dragHandle.png';
import useAlert from '../../hooks/useAlert';

import useDebounce from '../../hooks/useDebounce';

import { hasHttp, isUrl } from '../../util/validator';
import { trimBlankString } from '../../util/string';
 
type Props = {
	selectedContentId: number;
	currentContent: any;
}

function SelectedContentSns({ selectedContentId, currentContent }: Props) {
	const [dbItems, items, setItems] = useDebounce<any>([] as any, 300);
	const [errors, setErrors] = useState([] as any);

	const dispatch = useDispatch();
	const Alert = useAlert();

	const editor = useSelector((state: RootState) => state.editor);

	const currentContentStyle = currentContent?.content?.style?.[0];
	const styleTarget = `${selectedContentId}/style/0`;

	useEffect(() => {
		setItems(currentContent?.content.content[0].sns);		
	}, [currentContent]);

	useEffect(() => {
		if (dbItems.length === 0) {
			return;
		}
		// url 벨리데이션
		let errors:any = new Array(currentContent?.content.content[0].sns.length);
		dbItems.forEach((item:any, index:number) => {
			errors[index] = {
				required: false,
				url: false
			}
			if (item.url.length === 0) {
				errors[index]['required'] = true;
				return;
			}

			if(!(item.url === 'https://' || (hasHttp(item.url) && isUrl(item.url)))) {
				errors[index]['url'] = true;
				return;
			}
		});
		setErrors(errors);
		let valid = errors.some((item:any) => { return !item.required && !item.url });
		if (!valid) {
			return;
		}
		dispatch(editContent({ target: `${selectedContentId}/content/0/sns`, data: dbItems }));
	}, [dbItems])

	const moveEnd = (items:any) => {
		let nextData: any = produce(items, (draft: any) => { })

		dispatch(editContent({ target: `${selectedContentId}/content/0/sns`, data: nextData }));
	}

	const onChangeSelect = (e:any, cardIndex:number) => {
		const { value } = e.target;

		const nextData = produce(items, (draft: any) => {
			draft[cardIndex]['value'] = value;
			draft[cardIndex]['label'] = editor.option.snsList.find((item:any) => { return item.value === value })['label'];
		});

		dispatch(editContent({ target: `${selectedContentId}/content/0/sns`, data: nextData }));
	}

	const onChangeUrl = (e: any, cardIndex: number) => {
		const { value } = e.target;
		
		const nextData = produce(items, (draft: any) => {
			draft[cardIndex]['url'] = trimBlankString(value);
		});

		setItems(nextData);
	}

	const addItem = () => {
		if (items.length >= 10) {
			Alert.error(Alert?.msg?.SNS_OVER_LIMIT);
			return;
		}
		
		let newItem:any = produce(editor.option.snsList[0], (draft:any) => {
			draft['id'] = Math.max.apply(Math, items.map(function (o: any) { return o.id; })) + 1;
		});

		const nextData = produce(items, (draft: any) => {
			draft.push(newItem);
		});

		dispatch(editContent({ target: `${selectedContentId}/content/0/sns`, data: nextData }));
	}

	const removeItem = (cardId: number) => {
		if (items.length === 1) {
			return;
		}
		const nextData = produce(items, (draft: any) => {
			const index = draft.findIndex( (item:any)=> item.id === cardId);
			draft.splice(index, 1);
		});
		dispatch(editContent({ target: `${selectedContentId}/content/0/sns`, data: nextData }));
	}

	const reOrder = (list: any, startIndex: any, endIndex: any) => {
		const result = Array.from(list);
		const [removed] = result.splice(startIndex, 1);
		result.splice(endIndex, 0, removed);

		setItems(result);
		moveEnd(result);
	};

	const onDragEnd = (result: any) => {
		// dropped outside the list(리스트 밖으로 드랍한 경우)
		if (!result.destination) {
			return;
		}

		reOrder(
			items,
			result.source.index,
			result.destination.index
		);
	}

	const getItemStyle = (draggableStyle: any, isDragging: any) => ({
		userSelect: 'none',
		boxShadow: isDragging ? '0 0 10px 0 rgba(0,0,0,.4)' : 'none',
		...draggableStyle
	});

	return (
		<div className="editor-sidebar-selected-content-setting">
			<h3 className="field-section-title">버튼 스타일</h3>
			<FieldSection
				labelName=""
				label={false}
			>
				<Select
					target={`${styleTarget}/box/snsStyle`}
					value={currentContentStyle?.box.snsStyle}
					options={editor.option.snsStyle}
					size="full"
				/>
			</FieldSection>

			<h3 className="field-section-title">링크 항목</h3>
			<div className="inner-content-sort">
				<DragDropContext
					onDragEnd={onDragEnd}
				>
					<Droppable droppableId="layoutsort">
						{(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
							<div
								{...provided.droppableProps}
								ref={provided.innerRef}
							>
								{items.map((item: any, index: number) => (
									<div key={item.id} >
										<Draggable
											index={index}
											key={item.id}
											draggableId={`${item.id}`}
										>
											{
												(provided, snapshot) => (
													<div 
														className="inner-content-sort-item"
														ref={provided.innerRef}
														{...provided.draggableProps}
														style={getItemStyle(
															provided.draggableProps.style,
															snapshot.isDragging
														)}
													>
														<div
															{...provided.dragHandleProps}
															className="drag-handle"
														>
															<img src={dragHandle} alt="dragHandle" width="6" />
														</div>
														<div 
															className="sort-item-sns"	
														>
															<div className="sel">
																<img
																	src={`${service.resource}/editor/icon/sns/${item.value}-snsA.png`}
																	alt={item.label}
																	width="20"
																/>
																<select
																	value={item.value}
																	onChange={(e: any) => { onChangeSelect(e, index) }}
																>
																	{
																		editor.option.snsList.map((item: any, i: number) => (
																			<option
																				key={i}
																				value={item.value}
																			>
																				{item.label}
																			</option>
																		))
																	}
																</select>
															</div>
															<input
																type="url"
																value={item.url}
																maxLength={999}
																onChange={(e: any) => { onChangeUrl(e, index) }}
															/>
															{
																errors[index]?.required && (<span className="error-msg">{Alert?.msg?.REQUIRE_URL}</span>)
															}
															{
																errors[index]?.url && (<span className="error-msg">{Alert?.msg?.CHECK_URL_TYPE}</span>)
															}
															{
																items.length !== 1 && (
																	<div className="close">
																		<button
																			onClick={() => { removeItem(item.id) }}
																		>
																			<img src={close} width="12" alt="삭제" />
																		</button>
																	</div>
																)
															}
														</div>
													</div>
												)
											}
										</Draggable>
									</div>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>

				{/* <DndProvider backend={HTML5Backend}>
					{
						cards.map((card: any, index: number) => (
							<ContentSortItem
								key={index}
								id={card.id}
								card={card}
								moveCard={moveCard}
								moveEnd={moveEnd}
								findCard={findCard}
								active={card.active}
							>	
								<div className="sort-item-sns">
									<div className="sel">
										<img
											src={`${service.resource}/editor/icon/sns/${card.value}-snsA.png`}
											alt={card.label}
											width="20"
										/>
										<select
											value={card.value}
											onChange={(e: any) => { onChangeSelect(e, index)}}
										>
											{
												editor.option.snsList.map((item:any, i:number) => (
													<option
														key={i}
														value={item.value}
													>
														{item.label}
													</option>
												))
											}
										</select>
									</div>
									<input 
										type="url"
										value={card.url}
										onChange={(e: any) => { onChangeUrl(e, index) }}
									/>
									<div className="close">
										<button
											onClick={() => {removeItem(card.id)}}
										>
											<img src={close} width="12" alt="삭제"/>
										</button>
									</div>
								</div>
							</ContentSortItem>
						))
					}
				</DndProvider> */}
			</div>
			<div className="field-section">
				<div className="sns-field">
					<p>SNS 링크는 최대 10개까지 추가할 수 있습니다.</p>
					<button 
						className="btn medium"
						onClick={addItem}
					>SNS 링크 추가하기</button>
				</div>
			</div>
		</div>
	);
}

export default SelectedContentSns;